import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Assessment,
  CheckCircleOutline,
  HourglassEmpty,
  Inventory,
  Refresh,
  Visibility,
  ThumbUp,
  ThumbDown,
  UploadFile,
} from '@mui/icons-material';
import { authFetch } from '../api/authFetch';

/* ── Types ──────────────────────────────────────────────── */
interface DashboardStats {
  total_assessments: number;
  pending_reviews: number;
  approved: number;
  assets_released: number;
}

interface AssessmentRow {
  id: string;
  category: string;
  domain: string;
  score: number | null;
  status: string;
  created_at: string | null;
  user_email: string | null;
  user_name: string | null;
  organization_name: string | null;
  validation_status: string;
  validation_id: string | null;
}

/* ── Component ──────────────────────────────────────────── */
const ConsultantDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [assessments, setAssessments] = useState<AssessmentRow[]>([]);
  const [canUploadReport, setCanUploadReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Review dialog state
  const [reviewRow, setReviewRow] = useState<AssessmentRow | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  /* ── Data loading ───────────────────────────────────── */
  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await authFetch('/api/validation/dashboard-stats');
      const data = await res.json();
      if (data.success) {
        setStats(data.stats);
        setAssessments(data.assessments);
        setCanUploadReport(!!data.can_upload_report);
      } else {
        setError(data.error || 'Failed to load dashboard');
      }
    } catch {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  /* ── Approve / Reject handlers ──────────────────────── */
  const handleApprove = async () => {
    if (!reviewRow?.validation_id) return;
    setReviewLoading(true);
    try {
      const res = await authFetch(`/api/validation/${reviewRow.validation_id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consultantNotes: reviewNotes }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('Assessment approved and report sent to client.');
        setReviewRow(null);
        setReviewNotes('');
        loadDashboard();
      } else {
        setError(data.error || 'Approval failed');
      }
    } catch {
      setError('Failed to approve assessment');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleReject = async () => {
    if (!reviewRow?.validation_id) return;
    setReviewLoading(true);
    try {
      const res = await authFetch(`/api/validation/${reviewRow.validation_id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consultantNotes: reviewNotes }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('Assessment rejected with feedback.');
        setReviewRow(null);
        setReviewNotes('');
        loadDashboard();
      } else {
        setError(data.error || 'Rejection failed');
      }
    } catch {
      setError('Failed to reject assessment');
    } finally {
      setReviewLoading(false);
    }
  };

  /* ── Helpers ────────────────────────────────────────── */
  const validationColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending':
      case 'in_review': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  /* ── Render ─────────────────────────────────────────── */
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Assessment sx={{ mr: 1, color: '#00338D' }} />
        <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 600 }}>
          Human Validation Dashboard
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadDashboard}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* ── Stats cards ─────────────────────────────────── */}
      {stats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
          {[
            { label: 'Assessments Submitted', value: stats.total_assessments, icon: <Assessment />, color: '#00338D' },
            { label: 'Pending Reviews', value: stats.pending_reviews, icon: <HourglassEmpty />, color: '#E65100' },
            { label: 'Approved', value: stats.approved, icon: <CheckCircleOutline />, color: '#2E7D32' },
            { label: 'Assets Released', value: stats.assets_released, icon: <Inventory />, color: '#483698' },
          ].map((card) => (
            <Paper
              key={card.label}
              elevation={0}
              sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: card.color }}>
                {card.icon}
                <Typography variant="caption" sx={{ ml: 1, fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {card.label}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: card.color }}>
                {card.value}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* ── Assessments table ───────────────────────────── */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Assessment Review Queue
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review client-submitted assessments, approve results, or request revisions.
        {canUploadReport && ' As Engagement Manager you can also upload the final playback report.'}
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell><strong>Domain</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Client / Org</strong></TableCell>
              <TableCell><strong>Score</strong></TableCell>
              <TableCell><strong>Submitted</strong></TableCell>
              <TableCell><strong>Validation</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assessments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    No assessments have been submitted yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              assessments.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.domain}</TableCell>
                  <TableCell>{a.category}</TableCell>
                  <TableCell>
                    {a.user_name || a.user_email || '—'}
                    {a.organization_name && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {a.organization_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{a.score != null ? `${a.score.toFixed(1)}%` : '—'}</TableCell>
                  <TableCell>
                    {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={a.validation_status.replace('_', ' ')}
                      color={validationColor(a.validation_status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {a.validation_id && a.validation_status !== 'approved' && (
                        <Tooltip title="Review">
                          <IconButton size="small" onClick={() => { setReviewRow(a); setReviewNotes(''); }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canUploadReport && a.validation_status === 'approved' && (
                        <Tooltip title="Upload Final Report">
                          <IconButton
                            size="small"
                            sx={{ color: '#00338D' }}
                            onClick={() => {
                              setSuccess('Report upload feature coming soon — only the Engagement Manager can upload the final playback report.');
                            }}
                          >
                            <UploadFile fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Download Report">
                        <IconButton
                          size="small"
                          component="a"
                          href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/assessments/${a.id}/export.xlsx`}
                          target="_blank"
                        >
                          <Assessment fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Review dialog ───────────────────────────────── */}
      <Dialog open={!!reviewRow} onClose={() => setReviewRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#00338D' }}>
          Review Assessment — {reviewRow?.domain}
        </DialogTitle>
        <DialogContent dividers>
          {reviewRow && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body2">{reviewRow.category}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Domain</Typography>
                  <Typography variant="body2">{reviewRow.domain}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Client</Typography>
                  <Typography variant="body2">{reviewRow.user_name || reviewRow.user_email || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Organization</Typography>
                  <Typography variant="body2">{reviewRow.organization_name || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Score</Typography>
                  <Typography variant="body2">{reviewRow.score != null ? `${reviewRow.score.toFixed(1)}%` : '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Submitted</Typography>
                  <Typography variant="body2">
                    {reviewRow.created_at ? new Date(reviewRow.created_at).toLocaleDateString() : '—'}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <TextField
                label="Reviewer Notes"
                multiline
                rows={4}
                fullWidth
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add your expert insights, modifications, or feedback..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewRow(null)} disabled={reviewLoading}>Cancel</Button>
          <Button color="error" startIcon={<ThumbDown />} onClick={handleReject} disabled={reviewLoading}>
            Reject
          </Button>
          <Button
            variant="contained"
            startIcon={reviewLoading ? <CircularProgress size={16} /> : <ThumbUp />}
            onClick={handleApprove}
            disabled={reviewLoading}
            sx={{ backgroundColor: '#00338D' }}
          >
            Approve &amp; Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultantDashboard;
